/**
 * AeTodayCommandCenter.tsx — /#/today
 *
 * W66.X7 — Rebuilt per Bryan K. Short's locked doctrine 2026-06-03.
 *
 * TODAY = "Where am I today, and what am I doing today to improve tomorrow."
 * Quarterly + annual position and gap. 5x pipeline coverage rule of thumb (general math).
 * Today's strategic INTELLIGENCE moves on Top-10 accounts. Coach sitting next to the AE.
 * Bow-out is the AE's judgment — platform surfaces what they know/don't know.
 *
 * Layout (top to bottom):
 *   1. POSITION HEADER — 5-column annual quota strip
 *   2. COACH PROMPT BLOCK — "Today's questions." 3-5 generated questions
 *   3. TOP-10 STRATEGIC VIEW — table: account, CP, days at CP, last activity, verdict, open link
 *   4. YESTERDAY WINS — existing component
 *   5. CADENCE QUEUE — collapsed/de-emphasized at bottom, labeled "Cadence — optional tool"
 *
 * Canon:
 *   No exclamation points. No emojis. M-PACT hyphenated.
 *   "Checkpoint" not "Gate". CP format: "CP{N} — {Name}".
 *   5-color palette only: #6B7280, #DC2626, #EA580C, #EAB308, #16A34A.
 *   Tenant 1 only. No phantom defaults.
 *   Bryan K. Short. Demo AE 1 (never Geoff).
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { getSession, api } from "@/lib/session";
import { getImpersonationTarget } from "@/lib/impersonation";
import OperatorShell from "@/components/operator/OperatorShell";
import { ColorLegendFooter } from "@/components/ColorLegendFooter";
import { JumpstartStatusBlock } from "@/components/JumpstartStatusBlock";
import { useSprintConfig } from "@/lib/useSprintConfig";
import YesterdayWins from "@/components/ae-today/YesterdayWins";
import CadenceQueueV2 from "@/components/ae-today/CadenceQueueV2";

// ── Brand constants ───────────────────────────────────────────────────────────
const BG        = "#F9FAFB";
const CARD      = "#FFFFFF";
const BORDER    = "#E5E7EB";
const MI6_RED   = "#DC2626";
const ORANGE    = "#EA580C";
const YELLOW    = "#EAB308";
const GREEN     = "#16A34A";
const GRAY      = "#6B7280";
const TEXT_DARK = "#111827";
const TEXT_MID  = "#374151";
const TEXT_MUTE = "#6B7280";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt$(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function coverageColor(ratio: number): string {
  if (ratio >= 5) return GREEN;
  if (ratio >= 3) return YELLOW;
  return MI6_RED;
}

function gapColor(gap: number): string {
  return gap > 0 ? MI6_RED : GREEN;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface GapMathData {
  has_quota: boolean;
  annual_quota: number;
  quarter_quota: number;
  closed_ytd: number;
  commit: number;
  best_case: number;
  gap: number;
  weighted_pipeline: number;
  coverage_ratio: number;
  days_remaining: number;
  pace_pct: number;
  coach_cue: string;
}

interface Top10Account {
  rank: number;
  account_id: number;
  account_name: string;
  composite_score: number;
  next_cp: number | null;
  next_cp_label: string | null;
  days_stalled: number | null;
  forecast_bucket: string;
}

interface CadenceItemV2 {
  id: string;
  prospect_name: string;
  account_name: string;
  account_id: number | null;
  next_action_at: string;
  status: string;
  action_kind: string;
  notes: string | null;
  current_step: number;
  is_overdue: boolean;
  is_today: boolean;
  overdue_days: number;
  is_top_10: boolean;
}

interface WinEvent {
  event_type: "cp_answered" | "contact_added";
  account_name: string;
  account_id: number;
  cp_number: number | null;
  cp_label: string | null;
  contact_name?: string;
  contact_role?: string | null;
  occurred_at: string;
  label: string;
}

interface W50Payload {
  first_hour_directive: unknown | null;
  top_10_accounts: Top10Account[];
  cadence_due_v2: CadenceItemV2[];
  yesterday_wins: WinEvent[];
  coaching_inbox_v2: unknown[];
  meta: {
    fetched_at: string;
    today: string;
    yesterday: string;
    role: string;
    version: string;
  };
}

// ── 1. POSITION HEADER ────────────────────────────────────────────────────────
// W66.X7: added packPercentile prop — matches Cockpit Tile 1 pattern.
function PositionHeader({
  gapMath,
  loading,
  packPercentile,
}: {
  gapMath: GapMathData | null;
  loading: boolean;
  packPercentile: number | null;
}) {
  const year = new Date().getFullYear();

  const annual   = gapMath?.annual_quota ?? 0;
  const closed   = gapMath?.closed_ytd ?? 0;
  const gap      = gapMath?.gap ?? annual;
  const weighted = gapMath?.weighted_pipeline ?? 0;
  const coverage = gapMath?.coverage_ratio ?? 0;
  const quotaPct = annual > 0 ? Math.round((closed / annual) * 100) : null;

  if (loading) {
    return (
      <div
        style={{
          background: CARD,
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          padding: 20,
        }}
      >
        <div className="h-6 rounded animate-pulse w-1/2" style={{ background: BORDER }} />
      </div>
    );
  }

  if (!gapMath?.has_quota) {
    return (
      <div
        style={{
          background: CARD,
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          padding: 20,
        }}
      >
        <p style={{ fontSize: 13, color: TEXT_MUTE }}>
          No quota plan on file. Contact your manager.
        </p>
      </div>
    );
  }

  // Pack label for chip
  const packLabel = packPercentile !== null
    ? packPercentile >= 80 ? { text: "Top 20% on the team", color: GREEN }
    : packPercentile >= 40 ? { text: "Middle of pack", color: YELLOW }
    : { text: `Below pack — ${packPercentile}th percentile`, color: MI6_RED }
    : null;

  const tiles = [
    {
      label: `Annual Quota — ${year}`,
      value: fmt$(annual),
      color: TEXT_DARK,
    },
    {
      label: "Closed YTD",
      value: fmt$(closed),
      color: closed > 0 ? GREEN : TEXT_DARK,
    },
    {
      label: "Gap to Annual",
      value: fmt$(gap),
      color: gapColor(gap),
    },
    {
      label: "Weighted Pipeline",
      value: fmt$(weighted),
      color: TEXT_DARK,
    },
    {
      label: "Pipeline Coverage",
      value: `${coverage.toFixed(1)}x`,
      sub: "Rule of thumb: 5x",
      color: coverageColor(coverage),
    },
  ];

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: 20,
      }}
    >
      {/* W66.X7: % to quota + pack chip row at top */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 40, fontWeight: 800, color: TEXT_DARK, lineHeight: 1.0 }}>
            {quotaPct !== null ? `${quotaPct}%` : "—"}
          </span>
          <span style={{ fontSize: 12, color: TEXT_MUTE }}>to annual quota</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: coverageColor(coverage) }}>
          {coverage.toFixed(1)}x pipeline coverage
          <span style={{ fontSize: 11, color: TEXT_MUTE, fontWeight: 400, marginLeft: 6 }}>
            &middot; rule of thumb 5x
          </span>
        </div>
        {packLabel && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: `${packLabel.color}18`,
              border: `1px solid ${packLabel.color}40`,
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              color: packLabel.color,
            }}
          >
            {packLabel.text}
          </div>
        )}
      </div>

      {/* Detail tiles */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 16,
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 16,
        }}
        className="flex-wrap"
      >
        {tiles.map((t) => (
          <div key={t.label}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                color: TEXT_MUTE,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              {t.label}
            </div>
            <div
              style={{ fontSize: 22, fontWeight: 800, color: t.color, lineHeight: 1.1 }}
            >
              {t.value}
            </div>
            {t.sub && (
              <div style={{ fontSize: 10, color: TEXT_MUTE, marginTop: 2 }}>
                {t.sub}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 2. COACH PROMPT BLOCK ─────────────────────────────────────────────────────
function deriveCoachPrompts(accounts: Top10Account[]): string[] {
  const prompts: string[] = [];

  // Accounts stalled > 14 days
  const stalled = accounts.filter((a) => (a.days_stalled ?? 0) > 14);
  if (stalled.length > 0) {
    const a = stalled[0];
    prompts.push(
      `${a.account_name} has been at CP${a.next_cp ?? "?"} for ${a.days_stalled} days with no recent evidence. What is your next move?`
    );
  }

  // Accounts stalled > 21 days — push/hold/bow
  const deepStalled = accounts.filter((a) => (a.days_stalled ?? 0) > 21);
  if (deepStalled.length > 0) {
    const a = deepStalled[0];
    prompts.push(
      `${a.account_name} has been stalled for over three weeks. Push, hold, or bow out?`
    );
  }

  // Accounts at CP5 or higher without budget confirmed
  const highCp = accounts.filter((a) => (a.next_cp ?? 0) >= 5 && (a.composite_score ?? 0) < 60);
  if (highCp.length >= 2) {
    prompts.push(
      `${highCp.length} accounts are at CP5 or above without strong scoring. Which one are you putting energy into this week?`
    );
  }

  // Low-score accounts in top 10
  const atRisk = accounts.filter((a) => a.composite_score < 30 && a.composite_score > 0);
  if (atRisk.length > 0) {
    const a = atRisk[0];
    prompts.push(
      `${a.account_name} is scoring below 30. Is the buyer engagement still real, or is it time to bow out?`
    );
  }

  // Quiet day fallback
  if (prompts.length === 0) {
    prompts.push(
      "Quiet day. Your book is current. Use the time to expand contact coverage on your Top-10."
    );
  }

  return prompts.slice(0, 5);
}

function CoachPromptBlock({
  accounts,
  loading,
}: {
  accounts: Top10Account[];
  loading: boolean;
}) {
  const prompts = loading ? [] : deriveCoachPrompts(accounts);

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        Today's questions
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-4 rounded animate-pulse"
              style={{ background: BORDER, width: `${80 - i * 10}%` }}
            />
          ))}
        </div>
      ) : (
        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {prompts.map((p, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                gap: 10,
                paddingBottom: i < prompts.length - 1 ? 10 : 0,
                borderBottom: i < prompts.length - 1 ? `1px solid ${BORDER}` : "none",
                marginBottom: i < prompts.length - 1 ? 10 : 0,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: BORDER,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: TEXT_MID,
                  marginTop: 1,
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.5 }}>{p}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── 3. TOP-10 STRATEGIC VIEW ──────────────────────────────────────────────────
function stalledColor(days: number | null): string {
  if (days === null) return TEXT_MUTE;
  if (days >= 21) return MI6_RED;
  if (days >= 14) return YELLOW;
  return TEXT_MUTE;
}

function computeVerdict(a: Top10Account): { label: string; color: string } {
  const days = a.days_stalled ?? 0;
  const score = a.composite_score ?? 0;
  if (score < 20 || days > 30) return { label: "BOW", color: MI6_RED };
  if (days > 14 || score < 40) return { label: "GATHER", color: YELLOW };
  return { label: "PUSH", color: GREEN };
}

function Top10StrategicView({
  accounts,
  loading,
}: {
  accounts: Top10Account[];
  loading: boolean;
}) {
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <div
        style={{
          background: CARD,
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          padding: 20,
        }}
      >
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-10 rounded animate-pulse"
              style={{ background: BORDER, opacity: 0.6 - i * 0.1 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div
        style={{
          background: CARD,
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          padding: 20,
        }}
      >
        <p style={{ fontSize: 13, color: TEXT_MUTE }}>
          No Top-10 accounts on file. Add accounts and mark them is_top_10 to see your strategic view.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "14px 20px 10px",
          borderBottom: `1px solid ${BORDER}`,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
        }}
      >
        Top-10 Strategic View
      </div>

      {/* Table header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 60px 90px 100px 70px 60px",
          gap: 8,
          padding: "8px 20px",
          background: BG,
          borderBottom: `1px solid ${BORDER}`,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: TEXT_MUTE,
          textTransform: "uppercase",
        }}
      >
        <span>Account</span>
        <span>CP</span>
        <span>Days at CP</span>
        <span>Last Activity</span>
        <span>Verdict</span>
        <span></span>
      </div>

      {accounts.map((a) => {
        const verdict = computeVerdict(a);
        const days = a.days_stalled ?? null;
        const lastActivityText = days !== null ? `${days}d ago` : "—";

        return (
          <div
            key={a.account_id}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 60px 90px 100px 70px 60px",
              gap: 8,
              padding: "10px 20px",
              borderBottom: `1px solid ${BORDER}`,
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: TEXT_DARK,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {a.account_name}
            </span>

            <span style={{ fontSize: 12, color: TEXT_MID }}>
              CP{a.next_cp ?? "?"}
            </span>

            <span
              style={{
                fontSize: 12,
                color: stalledColor(days),
                fontWeight: days !== null && days >= 14 ? 700 : 400,
              }}
            >
              {days !== null ? `${days}d` : "—"}
            </span>

            <span style={{ fontSize: 12, color: TEXT_MUTE }}>
              {lastActivityText}
            </span>

            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: verdict.color,
                letterSpacing: "0.05em",
              }}
            >
              {verdict.label}
            </span>

            <button
              onClick={() => setLocation(`/account/${a.account_id}`)}
              style={{
                fontSize: 11,
                color: MI6_RED,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Open
            </button>
          </div>
        );
      })}

      <div
        style={{
          padding: "8px 20px",
          fontSize: 10,
          color: TEXT_MUTE,
        }}
      >
        Verdict is informational — push, gather intelligence, or bow out is your call.
        Days at CP in yellow &gt;14d, red &gt;21d.
      </div>
    </div>
  );
}

// ── 5. CADENCE QUEUE (collapsed, de-emphasized) ───────────────────────────────
function CadenceSection({
  items,
  loading,
}: {
  items: CadenceItemV2[];
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          background: "none",
          border: "none",
          cursor: "pointer",
          borderBottom: open ? `1px solid ${BORDER}` : "none",
        }}
      >
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: TEXT_MUTE,
              textTransform: "uppercase",
            }}
          >
            Cadence — optional tool
          </div>
          <div style={{ fontSize: 11, color: TEXT_MUTE, marginTop: 2 }}>
            Use this when you want to put a stalled opportunity on a structured reactivation cadence.
          </div>
        </div>
        <span style={{ fontSize: 12, color: TEXT_MUTE, flexShrink: 0, marginLeft: 12 }}>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 0 4px" }}>
          <CadenceQueueV2 items={items} loading={loading} />
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AeTodayCommandCenter() {
  const session = getSession();
  // W66.X15: effective identity — impersonation target overrides real session for
  // display labels. Data fetches already use X-Impersonate-User-Id header via api().
  const impersonationTarget = getImpersonationTarget();
  const repName = impersonationTarget?.name ?? session?.name ?? "Today";
  const role = impersonationTarget?.role ?? (session as any)?.role ?? "AE";

  const [payload, setPayload] = useState<W50Payload | null>(null);
  const [gapMath, setGapMath] = useState<GapMathData | null>(null);
  const [loading, setLoading] = useState(true);
  const [gapLoading, setGapLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // W66.X7: pack percentile — fetched from /api/team/position, null if unavailable
  const [packPercentile, setPackPercentile] = useState<number | null>(null);
  // T2 Item 5 — Jumpstart sprint config (used to seed JumpstartStatusBlock)
  const { config: sprintConfig } = useSprintConfig();

  useEffect(() => {
    api<W50Payload>("/api/today/w40")
      .then((res) => setPayload(res))
      .catch((e) => setError(e?.message ?? "Could not load today command center"))
      .finally(() => setLoading(false));

    api<GapMathData>("/api/today/gap-math")
      .then((res) => setGapMath(res))
      .catch(() => setGapMath(null))
      .finally(() => setGapLoading(false));

    // W66.X7: pack percentile from team position endpoint (best-effort)
    api<{ rank: number; total: number }>("/api/team/position")
      .then((data) => {
        if (data?.rank && data?.total && data.total > 0) {
          const pct = Math.round(((data.total - data.rank) / data.total) * 100);
          setPackPercentile(pct);
        }
      })
      .catch(() => { /* endpoint unavailable — no phantom data */ });
  }, []);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const roleLabel =
    role === "bdr" ? "BDR"
    : role === "ae" ? "AE"
    : role === "manager" ? "Manager"
    : role === "executive" ? "Executive"
    : role.toUpperCase();

  return (
    <OperatorShell breadcrumbs={[]}>
      <div className="min-h-screen" style={{ background: BG }}>

        {/* Page header */}
        <header
          className="w-full border-b px-6 py-4"
          style={{ background: CARD, borderColor: BORDER }}
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK }}>
                {repName}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: TEXT_MUTE,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                {roleLabel} — Today
              </div>
            </div>
            <div style={{ fontSize: 12, color: TEXT_MUTE }}>{today}</div>
          </div>
        </header>

        {/* Error banner */}
        {error && (
          <div
            className="mx-6 mt-6 px-4 py-3 rounded border text-sm"
            style={{ borderColor: MI6_RED, color: MI6_RED, background: "#FEF2F2" }}
          >
            {error}
          </div>
        )}

        {/* Page body */}
        <div className="px-4 py-6 space-y-5 max-w-5xl mx-auto pb-16">

          {/* 1. Position Header */}
          <PositionHeader gapMath={gapMath} loading={gapLoading} packPercentile={packPercentile} />

          {/* 2. Coach Prompt Block */}
          <CoachPromptBlock
            accounts={payload?.top_10_accounts ?? []}
            loading={loading}
          />

          {/* 3. Top-10 Strategic View */}
          <Top10StrategicView
            accounts={payload?.top_10_accounts ?? []}
            loading={loading}
          />

          {/* 4. Yesterday Wins */}
          <YesterdayWins
            wins={payload?.yesterday_wins ?? []}
            loading={loading}
          />

          {/* 5. Cadence Queue — removed from primary surface per W66.X7 doctrine.
              Cadence is accessible via the footer link below. */}

          {/* T2 Item 5 — Accelerated Jumpstart milestone track (rollup, keyed to sprint start) */}
          {sprintConfig?.sprintStart && (
            <JumpstartStatusBlock
              engagement_start_date={sprintConfig.sprintStart}
            />
          )}

        </div>

        {/* Brand footer — Cadence accessible here, not as a primary block (W66.X7) */}
        <footer
          className="w-full py-4 text-center text-xs border-t"
          style={{ color: TEXT_MUTE, borderColor: BORDER }}
        >
          Attilius — Mi6 Group
          <span style={{ margin: "0 8px", opacity: 0.4 }}>|</span>
          <a
            href="/#/cadence"
            style={{
              color: TEXT_MUTE,
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              cursor: "pointer",
            }}
          >
            Cadence (optional tool)
          </a>
        </footer>
        {/* T2 Item 4 — Color Legend Footer (Canon §19) */}
        <ColorLegendFooter />
      </div>
    </OperatorShell>
  );
}
